import {useMemo} from 'react';
import type {List} from 'immutable';

import type {ClassroomModelV1} from '@albert-io/models';
import {resource} from '@albert-io/json-api-framework/request/builder';

import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {
  useAllAdaptiveSubjects,
  useHomeAdaptiveSubjects
} from 'client/TeacherDashboard/Home/hooks/useHomeAdaptiveSubjects';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';

export interface EmptyStateSubject {
  id: string;
  name: string;
  urlSlug: string;
  /** kebab-cased curriculum area, keyed to the --color-background-domain-<slug> token. */
  curriculumAreaSlug: string;
}

const getClassSubjectsQuery = (classroomId: string) =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .include('subjects_v2')
    .filter({status: 'active', id: classroomId})
    .pageSize(1);

export const useAdaptiveEmptyStateSubjects = (
  classroomId?: string
): {subjects: EmptyStateSubject[]; loading: boolean} => {
  const {subjects: personalized, loading: personalizedLoading} = useHomeAdaptiveSubjects();
  const {subjects: catalog} = useAllAdaptiveSubjects();

  const classQuery = classroomId ? getClassSubjectsQuery(classroomId) : null;
  const {resources} = useMandarkQueries<List<ClassroomModelV1>>(classQuery ? [classQuery] : []);
  // `isResourceReady` settles on failure too; `isResourcePopulated` (what `useMandarkQueries`
  // reports) never does, which would pin this hook at loading forever after one failed request.
  const hasClassQuerySettled = !classQuery || classQuery.isResourceReady();

  const slugBySubjectId = useMemo(
    () => new Map(catalog.map((subject) => [subject.id, subject.curriculumAreaSlug])),
    [catalog]
  );

  const classAdaptiveSubjects = useMemo<EmptyStateSubject[]>(() => {
    const classroom = resources[0]?.first();
    if (!classroom) {
      return [];
    }
    return classroom
      .getSubjects()
      .filter((subject) => subject.isAdaptive())
      .map((subject) => ({
        id: subject.getId(),
        name: subject.getName(),
        urlSlug: subject.getUrlSlug(),
        curriculumAreaSlug: slugBySubjectId.get(subject.getId()) ?? 'default'
      }))
      .toArray();
  }, [resources, slugBySubjectId]);

  // Three tiers, most specific first — and never answer from a lower one until the tier above it
  // has actually come back empty, or the wrong subjects flash on screen first.
  if (!hasClassQuerySettled) {
    return {subjects: [], loading: true};
  }
  if (classAdaptiveSubjects.length > 0) {
    return {subjects: classAdaptiveSubjects, loading: false};
  }
  return {subjects: personalized, loading: personalizedLoading};
};
